let activePlayback: { root: HTMLElement; pause: () => void } | null = null;

export const setupYoutubeEmbeds = (scope: ParentNode = document) => {
  const roots = scope.querySelectorAll<HTMLElement>('[data-youtube-embed]');
  roots.forEach((root) => {
    if (root.dataset.youtubeInitialized === 'true') return;
    root.dataset.youtubeInitialized = 'true';

    const videoId = root.dataset.youtubeId;
    const playlistId = root.dataset.youtubePlaylistId;
    const thumbnail = root.querySelector<HTMLImageElement>(
      '[data-youtube-thumbnail]'
    );
    const playButton = root.querySelector<HTMLButtonElement>(
      '[data-youtube-play]'
    );

    const handleThumbnailError = () => {
      if (!thumbnail) return;
      // A late error from an in-flight thumbnail request can still fire
      // after the thumbnail was removed and playback started — ignore it
      // once a video is already showing, or it would hide the player.
      if (root.querySelector('iframe')) return;
      const fallbackSrc = thumbnail.dataset.youtubeThumbnailFallback;
      if (fallbackSrc && thumbnail.src !== fallbackSrc) {
        thumbnail.src = fallbackSrc;
        return;
      }
      root.style.display = 'none';
    };

    // YouTube serves a generic 120x90 grey placeholder (with an HTTP 404
    // status) for maxresdefault when that resolution was never generated —
    // the browser decodes it fine, so neither the `error` event nor a
    // naturalWidth of 0 catches it. Detect it by its fixed placeholder size.
    const isPlaceholderThumbnail = (img: HTMLImageElement) =>
      img.naturalWidth === 0 ||
      (img.naturalWidth === 120 && img.naturalHeight === 90);

    thumbnail?.addEventListener('error', handleThumbnailError);
    // The 404-with-valid-body placeholder decodes successfully, so the
    // browser fires `load`, not `error` — check its dimensions there too.
    thumbnail?.addEventListener('load', () => {
      if (thumbnail && isPlaceholderThumbnail(thumbnail)) {
        handleThumbnailError();
      }
    });
    // The deferred module can attach these listeners after the browser has
    // already finished loading (or failed to load) the image, in which case
    // neither `load` nor `error` fires again — check for that case directly.
    // `currentSrc` stays empty until the browser actually attempts to
    // resolve the image, so this skips lazy thumbnails that simply haven't
    // started loading yet (same complete/naturalWidth signature as a
    // genuine failure).
    if (
      thumbnail?.complete &&
      isPlaceholderThumbnail(thumbnail) &&
      thumbnail.currentSrc !== ''
    ) {
      handleThumbnailError();
    }

    // enablejsapi=1 lets the pause command below reach the player without
    // loading YouTube's full IFrame API script.
    const pausePlayback = () => {
      const iframe = root.querySelector<HTMLIFrameElement>('iframe');
      iframe?.contentWindow?.postMessage(
        JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }),
        '*'
      );
    };

    playButton?.addEventListener('click', () => {
      if (!videoId && !playlistId) return;

      if (activePlayback && activePlayback.root !== root) {
        activePlayback.pause();
      }

      const iframe = document.createElement('iframe');
      // cc_load_policy=0 keeps captions off by default, overriding the
      // viewer's own YouTube account setting. playsinline=1 stops iOS from
      // forcing fullscreen playback instead of staying in the wrapper.
      const embedPath = playlistId ? 'videoseries' : videoId;
      const listParam = playlistId ? `list=${playlistId}&` : '';
      iframe.src = `https://www.youtube.com/embed/${embedPath}?${listParam}autoplay=1&cc_load_policy=0&enablejsapi=1&playsinline=1`;
      iframe.title = 'YouTube video player';
      iframe.className = 'h-full w-full';
      iframe.allow = 'autoplay; fullscreen; picture-in-picture';
      iframe.allowFullscreen = true;
      // YouTube can return Error 153 when this isn't set explicitly.
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      // Inline styles beat the global `iframe[src*="youtube"]` rule in
      // theme.scss, which forces a 16:9 aspect ratio regardless of the
      // block's chosen ratio.
      iframe.style.aspectRatio = 'auto';
      iframe.style.height = '100%';

      root.querySelector('img')?.remove();
      playButton.remove();
      root.appendChild(iframe);
      activePlayback = { root, pause: pausePlayback };
    });
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setupYoutubeEmbeds();
  });
} else {
  setupYoutubeEmbeds();
}

document.addEventListener('shopify:section:load', (event) => {
  const target = (event as CustomEvent).target as HTMLElement | null;
  if (!target) return;
  if (!target.querySelector('[data-youtube-embed]')) return;

  target
    .querySelectorAll<HTMLElement>('[data-youtube-embed]')
    .forEach((root) => {
      delete root.dataset.youtubeInitialized;
    });
  setupYoutubeEmbeds(target);
});
